import { Component, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute, GuardsCheckEnd, NavigationEnd } from '@angular/router';
import { SessionService } from '../services/session.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AccountType } from '../models/AccountModel';
import { PlatformApiService } from '../services/platform-api.service';
import { ThemeManagerService } from '../services/theme-manager.service';
import { takeWhile } from 'rxjs/operators';
import { interval } from 'rxjs';

@Component({
  selector: 'cw-ci-working-account-selector',
  template: `
  <div style="flex: 1 auto" [ngStyle]="themeManager.getUnderlayStyle(true)">
  <mat-toolbar class="mat-elevation-z2" style="padding-bottom:0px; position:relative">
    <span *ngIf="_sessionService.getEnvNotification().label" matBadge="?" matBadgeSize="small" matBadgeColor="warn" [matTooltip]="_sessionService.getEnvNotification().desc">{{_sessionService.getEnvNotification().label}}</span>
    <a class="logo" [routerLink]="['/secured']" style="position: absolute; top:50%; transform: translateY(-50%); left: 0; right: 0; margin: auto;height: 50px; width: 250px;">
      <img src="{{themeManager.getLayoutLogo()}}" style='width:250px;'>
    </a>
    <button matTooltip="Account Options" matTooltipPosition="left" mat-icon-button [matMenuTriggerFor]="profileMenu" style="margin:0 0 0 auto;">
      <mat-icon>account_circle</mat-icon>
    </button>
    <mat-menu #profileMenu="matMenu" [overlapTrigger]="false">
      <button mat-menu-item [routerLink]="['/secured/profile']" color="primary">
        <mat-icon color="primary">account_circle</mat-icon>
        <span>Profile</span>
      </button>
      <button mat-menu-item [routerLink]="['/public/logout']" color="primary">
        <mat-icon color="primary">exit_to_app</mat-icon>
        <span>Log Out</span>
      </button>
    </mat-menu>
  </mat-toolbar>
  <mat-toolbar class="secondary-toolbar mat-elevation-z1">
  </mat-toolbar>
  <section class="main-content" [ngStyle]="themeManager.getUnderlayStyle(true)">

  <div class="dashboard-tile">
    <div class="tile-header">
      <div class="tile-header-title">
        Select the Account to Use
      </div>
    </div>
    <div class="tile-content">
      <img mat-card-image class="tile-logo" src="{{themeManager.getStackedLogo()}}" >
      <mat-nav-list>
        <mat-divider mat-line></mat-divider>
        <mat-list-item *ngFor="let cred of apiCredentials; let idx = index" (click)="selectCredential(idx)">
          <mat-icon matListIcon svgIcon="cw:action_next"></mat-icon>{{cred.account.name}}
        </mat-list-item>
      </mat-nav-list>
    </div>
  </div>
  </section>
  </div>

  `,
  styles: [`
  .dashboard-tile {
    max-width:470px;
    margin:auto;
    max-height:600px;
  }

  .dashboard-tile .tile-logo {
    width:60%;
    max-width:240px;
    padding:25px 0;
    margin:auto;
    display:block;
  }
  `]
})
export class WorkingAccountSelectorComponent implements OnInit {
  accounts: string[] = [];
  apiCredentials;

  constructor(public _sessionService: SessionService, public themeManager: ThemeManagerService, private _platformApi: PlatformApiService, private _router: Router) { }

  ngOnInit() {
    this._platformApi.getCredentials().subscribe(
      creds => {
        this._sessionService.setApiCredentials(creds);
        this.apiCredentials = this._sessionService.getApiCredentials()
      }, err => {
        this._sessionService.logout();
        this._router.navigate(['/']);
      }
    )
  }

  selectCredential(idx: number) {
    this._sessionService.setWorkingCredentials(idx);

    // For the time being, stored redirects will not be used for logins that require account selection. The problem that needs to be addressed
    // is that the requested redirect URL does not contain enough information to know if the access should be allowed to the account that was selected.
    // It is possible (likely) that the user is allowed to access the requested URL, but it's not a part of the account that they selected. This is a defficiecy in
    // our security model. API access should consider both the user AND the account that they select. Currently, we only verify the user and then use the API URL
    // to determine if the user has access.
    this._sessionService.doLoginRedirect('/secured', false);
  }
}

@Component({
  selector: 'cw-ci-secure-home-default',
  template: ''
})
export class SecureHomeDefaultRouterComponent {
  constructor(public _sessionService: SessionService, private _router: Router, private _activatedRoute: ActivatedRoute) {
    console.log('SecureHomeDefaultRouterComponent:constructor:activatedRoute', _activatedRoute, _router);
    const account = this._sessionService.getCurrentAccount();
    if (account.type === AccountType.SYSTEM) {
      console.log('Detected system user, routing to /admin');
      this._sessionService.doLoginRedirect('/secured/admin');
    } else if (this._sessionService.hasFeature('DASHBOARD_ENABLED')) {
      console.log('Regular account with dashboard enabled, routing based on account type.');
      if (this._sessionService.hasFeature('ALLOW_SUBACCOUNTS')) {
        this._sessionService.doLoginRedirect('/secured/master/companies');
      } else {
        this._sessionService.doLoginRedirect('/secured/master/internal/assessments');
      }
    } else {
      console.log('Regular account with dashboard not enabled, routing to company page.');
      this._router.navigate(['/secured/company']);
    }
  }
}

@Component({
  selector: 'cw-ci-secure-home',
  templateUrl: './secure-home.component.layout.html'
})
export class SecureHomeComponent implements OnInit, OnDestroy, AfterViewInit {
  title = '';
  alive: boolean;
  redirectUrl: string;

  constructor(public _sessionService: SessionService, public themeManager: ThemeManagerService, private _platformApi: PlatformApiService, private _router: Router, private _activatedRoute: ActivatedRoute, private _snackBar: MatSnackBar) {
    console.log('SecureHomeComponent:constructor');
    this.navigate();
    const self = this;
    _router.events.subscribe(e => {
      if (e instanceof GuardsCheckEnd) {
        if (!(e as GuardsCheckEnd).shouldActivate) {
          self._snackBar.open('You are not authorized to access that page', 'Dismiss', {
            duration: 3000
          });
        }
      }
    });
  }

  ngOnInit() {
    this.alive = true;

    // Initial session data
    this._sessionService.refreshSessionData();
  }

  ngOnDestroy() {
    this.alive = false;
  }

  ngAfterViewInit() {
    this.themeManager.updateThemeColors();
  }

  navigate() {
    console.log('SecureHomeComponent:navigate:activatedRoute', this._activatedRoute);
    if (this._sessionService.getWorkingCredential() == undefined) {
      console.log('No working account, routing to account selection.');
      this._router.navigate(['/secured/accountSelection']);
    } else {
      const account = this._sessionService.getCurrentAccount();
      console.log('SecureHomeComponent:navigate:account', account);
      if (account.planSubscription && account.planSubscription.plan.name.includes('Alliance Bernstein')) {
        console.log('Detected alliance bernstein, setting theme.');
        this.themeManager.setThemeClass('alliancebernstein-theme');
      }
    }
  }
}
